import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { CameraIcon } from '@heroicons/react/24/outline'

interface AppHeaderProps {
  children?: React.ReactNode
  title?: string
  subtitle?: string
}

const AppHeader = ({ children, title, subtitle }: AppHeaderProps) => {
  const navigate = useNavigate()

  return (
    <header className="relative z-20 glass-header">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 sm:py-6">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <motion.button 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 lg:space-x-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 lg:w-12 lg:h-12 glass-badge rounded-2xl flex items-center justify-center">
                <CameraIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <span className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                Shoot<span className="text-gray-400">Suite</span>
              </span>
            </motion.button>
            
            {/* Beta Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
              className="relative hidden sm:block"
            >
              <div className="relative">
                {/* Gradient border effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full blur-sm opacity-60 animate-pulse"></div>
                <div className="relative glass-badge rounded-full px-2 sm:px-3 py-1 border-2 border-transparent bg-gradient-to-r from-emerald-500/20 to-teal-500/20 backdrop-blur-sm">
                  {/* Gradient border overlay */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 opacity-50"></div>
                  <div className="absolute inset-[2px] rounded-full bg-black/80 backdrop-blur-sm"></div>
                  <div className="relative text-xs font-semibold text-white">
                    Now In Beta
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Mobile Beta Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
              className="relative sm:hidden"
            >
              <div className="relative">
                <div className="glass-badge rounded-full px-2 py-0.5 bg-gradient-to-r from-emerald-500/30 to-teal-500/30 backdrop-blur-sm border border-emerald-500/50">
                  <div className="text-xs font-semibold text-emerald-400">
                    Beta
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Optional title section */}
            {title && (
              <div className="ml-4 sm:ml-6 hidden md:block">
                <h1 className="text-xl lg:text-2xl font-bold text-white">{title}</h1>
                {subtitle && <p className="text-gray-400 text-sm">{subtitle}</p>}
              </div>
            )}
          </div>
          
          {/* Right side content (passed as children) */}
          {children && (
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-2 sm:space-x-4"
            >
              {children}
            </motion.div>
          )}
        </div>
      </nav>
    </header>
  )
}

export default AppHeader 